// src/scenes/car/CreateCar.jsx
import { useMemo, useState } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import SimpleForm from "../../components/SimpleForm";
import { Snackbar, Alert } from "@mui/material";

/* ---------- axios ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
});

/* ---------- helpers ---------- */
const buildInitials = (fields) =>
  fields.reduce((acc, f) => {
    const def =
      f.default ??
      (f.type === "checkbox"
        ? false
        : f.type === "number"
        ? ""
        : f.type === "date"
        ? ""
        : f.type === "file"
        ? null
        : "");
    acc[f.name] = def;
    return acc;
  }, {});

const buildSchema = (fields) => {
  const shape = {};
  fields.forEach((f) => {
    let rule;
    switch (f.type) {
      case "number":
        rule = yup.number().typeError("must be a number");
        break;
      case "checkbox":
        rule = yup.boolean();
        break;
      case "date":
        rule = yup.date().typeError("invalid date");
        break;
      case "file":
        rule = yup.mixed();
        break;
      default:
        rule = yup.string();
    }
    if (f.required) rule = rule.required("required");
    if (f.min != null) rule = rule.min(f.min);
    if (f.max != null) rule = rule.max(f.max);
    shape[f.name] = rule;
  });
  return yup.object().shape(shape);
};

async function postJSON(path, data, isFormData = false) {
  try {
    const res = await api.post(
      path,
      data,
      isFormData
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : undefined
    );
    return { data: res.data, error: null };
  } catch (err) {
    const serverErrors =
      err?.response?.data?.errors ||
      err?.response?.data?.message ||
      err.message;
    return { data: null, error: serverErrors };
  }
}

/* ---------- page ---------- */
export default function CreateCar() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });

  const FIELDS = useMemo(() => {
    return [
      {
        name: "carName",
        label: "Car Name",
        type: "text",
        span: 2,
        required: true,
        props: { placeholder: "e.g. Toyota Corolla" },
      },
      {
        name: "carPlate",
        label: "Plate Number",
        type: "text",
        span: 2,
        required: true,
        props: { placeholder: "e.g. ABC-123" },
      },
      {
        name: "carModel",
        label: "Model Year",
        type: "text",
        span: 1,
        required: true,
        props: { placeholder: "e.g. 2020" },
      },
      {
        name: "driverName",
        label: "Driver Name",
        type: "text",
        span: 1,
        required: true,
        props: { placeholder: "e.g. John Doe" },
      },
      {
        name: "driverPhone",
        label: "Driver Phone",
        type: "text",
        span: 2,
        required: true,
        props: { placeholder: "e.g. 0912345678" },
      },
      {
        name: "carFiles",
        label: "Car Files (optional)",
        type: "file",
        span: 4,
        required: false,
        onChange: (e, { setFieldValue }) => {
          const files = e.currentTarget.files;
          setFieldValue("carFiles", files && files.length ? files : null);
        },
        props: {
          style: { paddingTop: 10, paddingBottom: 10 },
          value: undefined,
          inputProps: {
            multiple: true,
            // accept: ".pdf",
            accept: ".pdf,.jpg,.jpeg,.png,.doc,.docx",
          },
          helperText: "Upload registration, insurance, photos, etc.",
        },
      },
    ];
  }, []);

  const INITIALS = useMemo(() => buildInitials(FIELDS), [FIELDS]);
  const SCHEMA = useMemo(() => buildSchema(FIELDS), [FIELDS]);

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, resetForm }
  ) => {
    // ✅ Normalize and validate Ethiopian phone numbers
    let phone = values.driverPhone.toString().trim();
    phone = phone.replace(/\D/g, ""); // remove all non-digit characters

    if (phone.startsWith("07")) {
      phone = "+2517" + phone.slice(2);
    } else if (phone.startsWith("7")) {
      phone = "+2517" + phone.slice(1);
    } else if (phone.startsWith("09")) {
      phone = "+2519" + phone.slice(2);
    } else if (phone.startsWith("9")) {
      phone = "+2519" + phone.slice(1);
    } else if (phone.startsWith("2517")) {
      phone = "+2517" + phone.slice(4);
    } else if (phone.startsWith("2519")) {
      phone = "+2519" + phone.slice(4);
    }

    // ensure proper Ethiopian format
    const isEthiopian = /^\+251[79]\d{8}$/;
    if (!isEthiopian.test(phone)) {
      setToast({
        open: true,
        msg: "Invalid phone number! Please enter a valid Ethiopian number.",
        sev: "error",
      });
      setSubmitting(false);
      return;
    }

    const hasFiles = values.carFiles && values.carFiles.length;

    if (hasFiles) {
      const formData = new FormData();
      formData.append("carName", values.carName || "");
      formData.append("carPlate", values.carPlate || "");
      formData.append("carModel", values.carModel || "");
      formData.append("driverName", values.driverName || "");
      formData.append("driverPhone", phone);
      Array.from(values.carFiles).forEach((f) =>
        formData.append("carFiles", f)
      );

      const { data, error } = await postJSON("/api/carInfo", formData, true);
      setSubmitting(false);

      if (error) {
        if (typeof error === "object" && !Array.isArray(error))
          setErrors(error);
        setToast({ open: true, msg: "Failed to create car", sev: "error" });
        return;
      }

      const msg = data?.message || "Car created successfully";
      setToast({ open: true, msg, sev: "success" });
      resetForm();
    } else {
      const payload = {
        carName: values.carName,
        carPlate: values.carPlate,
        carModel: values.carModel,
        driverName: values.driverName,
        driverPhone: phone,
      };

      const { data, error } = await postJSON("/api/carInfo", payload, false);
      setSubmitting(false);

      if (error) {
        if (typeof error === "object" && !Array.isArray(error))
          setErrors(error);
        setToast({ open: true, msg: "Failed to create car", sev: "error" });
        return;
      }

      const msg = data?.message || "Car created successfully";
      setToast({ open: true, msg, sev: "success" });
      resetForm();
    }
  };

  return (
    <>
      <SimpleForm
        title="CREATE CAR"
        subtitle="Register a car and driver"
        Header={Header}
        fields={FIELDS}
        initialValues={INITIALS}
        validation={SCHEMA}
        onSubmit={handleSubmit}
        submitLabel="Create Car"
        columns={4}
      />

      <Snackbar
       anchorOrigin={{ vertical: 'top', horizontal: 'right'}}
      sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
